/**
 * Global LF behaviour, loaded on every page: drawer, header, announcement rotator, footer, the cart/filter scrim click,
 * the delivery estimate and low-stock elements (Horizon's quick-add dialog can show a product on any page) and the scrollbar tracker.
 * Page-specific elements live in their own modules, loaded by the sections that use them: lf-carousel.js, lf-home.js (slideshow),
 * lf-tabs.js, lf-collection.js (load more), lf-modal.js (size guide), lf-cart.js (pairs well with) and lf-pages.js (form note).
 */
import { wrapIndex, formatEta, headerScrollState, productSelectOutcome, scrollbarOwner } from './lf-utils.js';
import { FOCUSABLE, openDrawers, releaseScrollLock, trapTab } from './lf-shared.js';

/**
 * Side drawer with a scrim, focus trap and Escape handling.
 *
 * Markup contract: `[data-lf-drawer-scrim]` and `.lf-drawer__panel` (tabindex="-1") as children, with optional
 * `[data-lf-drawer-close]` buttons inside the panel. Any element with `aria-controls="<drawer id>"` toggles the
 * drawer and has its `aria-expanded` kept in sync. Attribute `side` is `left` (default) or `right`.
 */
class LfDrawer extends HTMLElement {
  /** @type {HTMLElement | null} */
  #restoreTo = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#syncTriggers(this.isOpen);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.close({ restoreFocus: false });
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  get panel() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.lf-drawer__panel'));
  }

  /**
   * @param {Element | null} [trigger] - The control that opened the drawer; focus returns to it on close.
   */
  open(trigger = null) {
    if (this.isOpen) return;

    for (const other of openDrawers) other.close({ restoreFocus: false });

    const active = document.activeElement;
    this.#restoreTo = trigger instanceof HTMLElement ? trigger : active instanceof HTMLElement ? active : null;

    openDrawers.add(this);
    this.setAttribute('open', '');
    this.#syncTriggers(true);
    document.documentElement.classList.add('lf-scroll-locked');
    document.addEventListener('keydown', this.#onKeydown);

    const [first] = this.#focusables();
    (first ?? this.panel)?.focus({ preventScroll: true });

    this.dispatchEvent(new CustomEvent('lf-drawer:open', { bubbles: true }));
  }

  /**
   * @param {{ restoreFocus?: boolean }} [options]
   */
  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) return;

    this.removeAttribute('open');
    openDrawers.delete(this);
    releaseScrollLock();
    document.removeEventListener('keydown', this.#onKeydown);
    this.#syncTriggers(false);

    const target = this.#restoreTo;
    this.#restoreTo = null;
    if (restoreFocus && target?.isConnected) target.focus({ preventScroll: true });

    this.dispatchEvent(new CustomEvent('lf-drawer:close', { bubbles: true }));
  }

  /** @param {boolean} expanded */
  #syncTriggers(expanded) {
    if (!this.id) return;
    for (const trigger of document.querySelectorAll(`[aria-controls="${CSS.escape(this.id)}"]`)) {
      trigger.setAttribute('aria-expanded', String(expanded));
    }
  }

  #focusables() {
    const panel = this.panel;
    if (!panel) return [];
    return /** @type {HTMLElement[]} */ ([...panel.querySelectorAll(FOCUSABLE)]).filter(
      (element) => element.getClientRects().length > 0
    );
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-lf-drawer-scrim], [data-lf-drawer-close]')) {
      this.close();
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      if (event.defaultPrevented) return;
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    const panel = this.panel;
    if (panel) trapTab(event, panel, this.#focusables());
  };
}

document.addEventListener('click', (event) => {
  if (event.defaultPrevented || !(event.target instanceof Element)) return;

  const trigger = event.target.closest('[aria-controls]');
  const drawer = trigger ? document.getElementById(trigger.getAttribute('aria-controls') ?? '') : null;
  if (!(drawer instanceof LfDrawer)) return;

  event.preventDefault();
  if (drawer.isOpen) drawer.close();
  else drawer.open(trigger);
});

/**
 * Sticky site header that slides away on scroll down (past 240px) and returns on scroll up.
 * It publishes its state as `body.lf-hdr-hidden` so sticky toolbars can take its place.
 *
 * Horizon scrolls `.page-wrapper` instead of the document from 990px, and scroll events do not bubble,
 * so the listener sits on the document in the capture phase and accepts either scroller.
 */
class LfHeader extends HTMLElement {
  #lastY = 0;
  #queued = false;
  #hidden = false;

  connectedCallback() {
    const scroller = document.querySelector('.page-wrapper');
    this.#lastY = Math.max(document.scrollingElement?.scrollTop ?? 0, scroller?.scrollTop ?? 0);
    document.addEventListener('scroll', this.#onScroll, { capture: true, passive: true });
    this.addEventListener('focusin', this.#onFocusIn);
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.#onScroll, { capture: true });
    this.removeEventListener('focusin', this.#onFocusIn);
    this.#setHidden(false);
  }

  /** @param {Event} event */
  #onScroll = (event) => {
    const target = event.target;
    const scroller =
      target === document
        ? document.scrollingElement
        : target instanceof Element && target.matches('.page-wrapper')
          ? target
          : null;
    if (!scroller || this.#queued) return;

    this.#queued = true;
    requestAnimationFrame(() => {
      this.#queued = false;
      this.#update(scroller.scrollTop);
    });
  };

  /** @param {number} y */
  #update(y) {
    const next = headerScrollState({ y, lastY: this.#lastY, hidden: this.#hidden, drawersOpen: openDrawers.size > 0 });
    this.#lastY = next.lastY;
    this.#setHidden(next.hidden);
  }

  #onFocusIn = () => this.#setHidden(false);

  /** @param {boolean} hidden */
  #setHidden(hidden) {
    if (hidden === this.#hidden) return;
    this.#hidden = hidden;
    document.body.classList.toggle('lf-hdr-hidden', hidden);
  }
}

const ROTATE_EVERY = 4000;

/**
 * Shows one child at a time and cross-fades to the next every 4 seconds (styles live with the section).
 * Rotation stops under prefers-reduced-motion, while a mouse hovers the bar, while focus is inside it and while the tab is hidden.
 */
class LfRotator extends HTMLElement {
  /** @type {HTMLElement[]} */
  #items = [];
  #index = 0;
  #timer = 0;
  #hovered = false;
  #focused = false;
  #editing = false;
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#items = /** @type {HTMLElement[]} */ ([...this.children]);
    this.#show(0);
    this.setAttribute('data-ready', '');

    this.#motion.addEventListener('change', this.#sync);
    document.addEventListener('visibilitychange', this.#sync);
    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('shopify:block:select', this.#onBlockSelect);
    this.addEventListener('shopify:block:deselect', this.#onBlockDeselect);
    this.#sync();
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
    this.#motion.removeEventListener('change', this.#sync);
    document.removeEventListener('visibilitychange', this.#sync);
    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.removeEventListener('shopify:block:select', this.#onBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
  }

  /** @param {number} index */
  #show(index) {
    this.#index = index;
    this.#items.forEach((item, position) => {
      const active = position === index;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-hidden', String(!active));
    });
  }

  #sync = () => {
    window.clearInterval(this.#timer);
    this.#timer = 0;
    if (this.#items.length < 2 || this.#motion.matches || this.#hovered || this.#focused || this.#editing || document.hidden) return;
    this.#timer = window.setInterval(() => this.#show(wrapIndex(this.#index, 1, this.#items.length)), ROTATE_EVERY);
  };

  /** @param {PointerEvent} event */
  #onPointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#hovered = true;
    this.#sync();
  };

  #onPointerLeave = () => {
    this.#hovered = false;
    this.#sync();
  };

  #onFocusIn = () => {
    this.#focused = true;
    this.#sync();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#focused = false;
    this.#sync();
  };

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    const position = this.#items.indexOf(/** @type {HTMLElement} */ (event.target));
    if (position === -1) return;
    this.#editing = true;
    this.#show(position);
    this.#sync();
  };

  #onBlockDeselect = () => {
    this.#editing = false;
    this.#sync();
  };
}

const FOOTER_WIDE = '(min-width: 700px)';

/**
 * Footer link rows. Below 700px they are <details> accordions with the first one open; from 700px they are plain
 * columns, so every row is forced open and its summary stops toggling, leaves the tab order and is marked aria-disabled.
 */
class LfFooter extends HTMLElement {
  #query = window.matchMedia(FOOTER_WIDE);

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#query.addEventListener('change', this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#query.removeEventListener('change', this.#sync);
  }

  #sync = () => {
    const wide = this.#query.matches;
    this.querySelectorAll('details').forEach((row, index) => {
      row.open = wide || index === 0;
      const summary = row.querySelector('summary');
      if (!summary) return;
      // Wide: the rows are plain columns, so the summary is neither tabbable nor announced as a working button.
      if (wide) {
        summary.setAttribute('tabindex', '-1');
        summary.setAttribute('aria-disabled', 'true');
      } else {
        summary.removeAttribute('tabindex');
        summary.removeAttribute('aria-disabled');
      }
    });
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (this.#query.matches && event.target instanceof Element && event.target.closest('summary')) event.preventDefault();
  };
}

/**
 * Horizon's filter and cart drawers are non-modal sidebars from 990px, so they have no backdrop. lf-collection.css and lf-cart.css
 * draw a scrim on the drawer element itself; a click on that scrim lands on the element (never on the panel), which closes it.
 */
document.addEventListener('click', (event) => {
  const target = event.target;
  if (target instanceof HTMLElement && ['filters-drawer', 'cart-drawer'].includes(target.id) && target.hasAttribute('open') && 'close' in target) {
    /** @type {{ close: () => void }} */ (/** @type {unknown} */ (target)).close();
  }
});

/**
 * Those same non-modal drawers let Tab walk out of the open drawer into the page behind the scrim. From 990px, while focus is
 * inside one, Tab wraps within its panel instead. Below 990px they are modal and the browser keeps focus in; a quick-add dialog
 * opened from the cart is its own modal (its focus is in a different dialog) and traps itself.
 */
const SIDEBAR_WIDE = window.matchMedia('(min-width: 990px)');

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Tab' || event.defaultPrevented || !SIDEBAR_WIDE.matches) return;
  const active = document.activeElement;
  const drawer = active instanceof Element ? active.closest('#filters-drawer[open], #cart-drawer[open]') : null;
  const panel = drawer?.querySelector('dialog');
  if (!(panel instanceof HTMLElement) || /** @type {Element} */ (active).closest('dialog') !== panel) return;
  const focusables = /** @type {HTMLElement[]} */ ([...panel.querySelectorAll(FOCUSABLE)]).filter((element) => element.getClientRects().length > 0);
  trapTab(event, panel, focusables);
});

/**
 * Horizon reopens a theme-drawer that was open when the shopper navigated, because its sidebar mode keeps the page usable beside
 * it. The cart drawer overlays the page under a scrim at every width, and an overlay that follows the shopper to the next page
 * (after tapping a category link in the empty cart, for example) is wrong, so it opts out of that persistence.
 */
document.getElementById('cart-drawer')?.setAttribute('no-persist', '');

if (!customElements.get('lf-drawer')) customElements.define('lf-drawer', LfDrawer);
if (!customElements.get('lf-header')) customElements.define('lf-header', LfHeader);
if (!customElements.get('lf-rotator')) customElements.define('lf-rotator', LfRotator);
if (!customElements.get('lf-footer')) customElements.define('lf-footer', LfFooter);

/**
 * Turns the server's "Delivered in N–M working days" into real dates counted from the shopper's own calendar day
 * (Sunday never counts): "Order today, delivered between Mon 19 Oct – Wed 21 Oct".
 */
class LfDeliveryEstimate extends HTMLElement {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    const min = Number(this.dataset.minDays);
    const max = Number(this.dataset.maxDays);
    const text = this.querySelector('.lf-eta__text');
    if (text && min >= 1 && max >= min) {
      const dates = document.createElement('strong');
      dates.textContent = formatEta(new Date(), min, max);
      text.replaceChildren(`${this.dataset.prefix || 'Order today, delivered between'} `, dates);
    }

    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener('shopify:product:select', this.#onSelect);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener('shopify:product:select', this.#onSelect);
    this.#section = null;
  }

  /**
   * A sold-out variant promises no delivery, and neither does a combination that does not exist: follow the server's `hidden`
   * for the newly selected variant, and hide when there is no real variant.
   * @param {Event & { selectedOptions?: { value: string }[], promise?: Promise<{ detail?: any }> }} event
   */
  #onSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (!event.promise || typeof event.promise.then !== 'function') return;

    event.promise
      .then(({ detail }) => {
        const outcome = productSelectOutcome({ ownProductId: this.dataset.productId, detail, selectedOptions: event.selectedOptions });
        if (outcome === 'ignore') return;
        if (detail?.newProduct?.id) this.dataset.productId = String(detail.newProduct.id);
        if (outcome === 'clear') {
          this.hidden = true;
          return;
        }
        const next = detail?.html?.querySelector('lf-delivery-estimate');
        if (next) this.hidden = next.hasAttribute('hidden');
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[lf-delivery-estimate] Event promise rejected:', error);
      });
  };
}

/**
 * "Only N left". The server renders it for the selected variant; on every variant change Horizon fetches the section again and
 * this takes the fresh copy from that response, the same way Horizon's own inventory element does.
 */
class LfLowStock extends HTMLElement {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener('shopify:product:select', this.#onSelect);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener('shopify:product:select', this.#onSelect);
    this.#section = null;
  }

  /** @param {Event & { selectedOptions?: { value: string }[], promise?: Promise<{ detail?: any }> }} event */
  #onSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (!event.promise || typeof event.promise.then !== 'function') return;

    event.promise
      .then(({ detail }) => {
        const outcome = productSelectOutcome({ ownProductId: this.dataset.productId, detail, selectedOptions: event.selectedOptions });
        if (outcome === 'ignore') return;
        if (detail?.newProduct?.id) this.dataset.productId = String(detail.newProduct.id);
        if (outcome === 'clear') {
          this.replaceChildren();
          return;
        }
        const next = detail?.html?.querySelector('lf-low-stock');
        if (!next) return;
        this.replaceChildren(...[...next.childNodes].map((node) => document.importNode(node, true)));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[lf-low-stock] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('lf-delivery-estimate')) customElements.define('lf-delivery-estimate', LfDeliveryEstimate);
if (!customElements.get('lf-low-stock')) customElements.define('lf-low-stock', LfLowStock);

/**
 * Publishes `html[data-lf-scrollbar]` ("page", "root" or absent): which scroller currently shows a classic scrollbar,
 * and `--lf-sbw`, the width of the one in .page-wrapper.
 * lf-base.css keeps that strip reserved while a drawer or modal locks scrolling, so nothing behind it shifts.
 * Locks come from Horizon (html[scroll-lock]), from lf-drawer and lf-modal, and from the cart drawer's own CSS, so the
 * value is tracked while the page is unlocked rather than measured at the moment of locking. ResizeObserver covers a
 * scrollbar appearing or disappearing: it changes the scroller's content width.
 */
const pageScroll = window.matchMedia('(min-width: 990px)');

function syncScrollbar() {
  const root = document.documentElement;
  const scroller = pageScroll.matches ? document.querySelector('.page-wrapper') : root;
  if (!scroller) return;

  const barWidth = scroller === root ? window.innerWidth - root.clientWidth : scroller.offsetWidth - scroller.clientWidth;
  const owner = scrollbarOwner(pageScroll.matches, getComputedStyle(scroller).overflowY, barWidth);
  if (owner === null) return;

  if (owner) root.setAttribute('data-lf-scrollbar', owner);
  else root.removeAttribute('data-lf-scrollbar');

  // Fixed bars measure from the viewport, which includes the strip .page-wrapper scrolls in, so they stop short of it.
  if (owner === 'page') root.style.setProperty('--lf-sbw', `${barWidth}px`);
  else root.style.removeProperty('--lf-sbw');
}

syncScrollbar();
pageScroll.addEventListener('change', () => {
  // The scroller changed (document <-> .page-wrapper). If the new one is locked it reports nothing, and the old owner's flag would
  // stay behind on the wrong element; unlocked, the sync below sets the right one straight away.
  document.documentElement.removeAttribute('data-lf-scrollbar');
  document.documentElement.style.removeProperty('--lf-sbw');
  syncScrollbar();
});
if (typeof ResizeObserver === 'function') {
  const scrollbarObserver = new ResizeObserver(syncScrollbar);
  scrollbarObserver.observe(document.documentElement);
  const page = document.querySelector('.page-wrapper');
  if (page) scrollbarObserver.observe(page);
}
